import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { PageLayout } from '../components/layout';
import { CreatePostModal } from '../components/posts/CreatePostModal';
import { Users, Plus, BarChart2 } from 'lucide-react';
import { SocialIcon } from '../components/icons/SocialIcon';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { useBrands } from '../hooks/useBrands';
import { LinkBrandRedirectionCard } from '../components/common/LinkBrandRedirectionCard';
import { fetchManagementDashboard } from '../services/managementService';
import { DraftsCards } from '../components/dashboard/DraftsCards';
import { TopPostsCard } from '../components/dashboard/TopPostsCard';
import { BrandTabs } from '../components/dashboard/BrandTabs';
import { PersonasCard } from '../components/dashboard/PersonasCard';
import { BrandSummaryCard } from '../components/brands/BrandSummaryCard';
import type { BrandSummaryPayload } from '../components/brands/BrandSummaryCard';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { data: brandsData, isLoading: isBrandsLoading } = useBrands();
  const [selectedBrandId, setSelectedBrandId] = useState<string>(''); // empty = All Brands

  useEffect(() => {
    document.title = t('dashboard.pageTitle', 'Dashboard');
  }, [t]);

  // If there is only one brand, auto-select it and hide tabs
  useEffect(() => {
    const list = Array.isArray(brandsData?.brands) ? brandsData!.brands : [];
    if (list.length === 1) {
      const onlyId = list[0]?.id || '';
      if (onlyId && selectedBrandId !== onlyId) {
        setSelectedBrandId(onlyId);
      }
    }
  }, [brandsData, selectedBrandId]);

  // Get first name and capitalize it
  const namePart = user?.name ? user.name.split(' ')[0] : '';
  const firstName = namePart.charAt(0).toUpperCase() + namePart.slice(1);

  const bannerTitle = t('dashboard.title', "Hello {{firstName}}, Here's your Social Media overview", { firstName: firstName || 'there' });
  const bannerSubtitle = t('dashboard.subtitle', 'This is your content command center. Track, plan and optimize your content with the help of AI insights and recommendations.');

  const [followersData, setFollowersData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoading = isLoading || (!followersData && !error);
  const [recentDrafts, setRecentDrafts] = useState<any[]>([]);
  const [draftsByBrand, setDraftsByBrand] = useState<Record<string, any[]>>({});
  const [topPosts, setTopPosts] = useState<any[]>([]);
  const [topPostsByBrand, setTopPostsByBrand] = useState<Record<string, any[]>>({});
  const [personasByBrand, setPersonasByBrand] = useState<Record<string, any[]>>({});
  const [allPersonas, setAllPersonas] = useState<any[]>([]);
  const [brandSummariesByBrand, setBrandSummariesByBrand] = useState<Record<string, BrandSummaryPayload>>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchFollowers = async () => {
      setIsLoading(true); setError(null);
      const minDelayMs = 600; // ensure skeleton is visible briefly to avoid flicker
      const startAt = Date.now();
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      try {
        const lang = (typeof navigator?.language === 'string' ? navigator.language.split('-')[0] : 'en') || 'en';
        // Call new management dashboard endpoint
        const json = await fetchManagementDashboard(lang);
        console.log('[Dashboard] /management/dashboard response', json);
        if (cancelled) return;
        // New schema: array of brands with either
        // - metrics.byPlatform & metrics.totalFollowers (old)
        // - followers.followers { total, linkedin: [..], facebook: [..], instagram: [..], ... } (new)
        const brandsArray: any[] = Array.isArray(json?.brands) ? json.brands : [];
        const brandsObj: Record<string, any> = {};
        const draftsAccumulator: any[] = [];
        const draftsAccumulatorByBrand: Record<string, any[]> = {};
        const personasAccumulatorByBrand: Record<string, any[]> = {};
        const allPersonasAccumulator: any[] = [];
        const summariesAccumulatorByBrand: Record<string, BrandSummaryPayload> = {};
        for (const b of brandsArray) {
          const followersBlock = b?.followers?.followers || null;
          const byPlatform = b?.metrics?.byPlatform || {};

          // Prefer followersBlock if provided; fallback to metrics.byPlatform
          let followersObj: any = followersBlock ? { ...followersBlock } : {
            total: b?.metrics?.totalFollowers,
            youtube: byPlatform.youtube,
            facebook: byPlatform.facebook,
            instagram: byPlatform.instagram,
            linkedin: byPlatform.linkedin,
            tiktok: byPlatform.tiktok,
          };

          // Ensure total is a number. If missing, compute from platform values (sum numbers or arrays)
          if (typeof followersObj.total !== 'number') {
            const platformsSum = Object.entries(followersObj)
              .filter(([k]) => k !== 'total')
              .reduce((sum, [, v]: [string, any]) => {
                if (Array.isArray(v)) return sum + v.reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0);
                if (typeof v === 'number') return sum + v;
                return sum;
              }, 0);
            followersObj.total = platformsSum;
          }

          brandsObj[b.id] = {
            brandName: b.name,
            brandLogo: b.logo,
            followers: followersObj,
          };

          // Collect brand summary payload per brand for the new BrandSummaryCard
          try {
            summariesAccumulatorByBrand[b.id] = {
              id: b.id,
              name: b.name,
              logo: b.logo,
              brand: b.brand || {},
            } as BrandSummaryPayload;
          } catch {
            // noop; be resilient if shape changes
          }

          // Collect recent drafts for the new DraftsCards section (and index by brand)
          if (Array.isArray(b?.recentDrafts)) {
            const mappedDrafts = b.recentDrafts.map((d: any) => ({
              id: d.id,
              content: d.content || d.title || '',
              media: d.media || null,
              platforms: Array.isArray(d.platforms)
                ? d.platforms
                : (d.platforms ? Object.keys(d.platforms) : []),
              brandId: d.brandId || b.id,
              brandName: d.brandName || b.name,
              brandLogo: d.brandLogo || b.logo,
            }));
            draftsAccumulator.push(...mappedDrafts);
            draftsAccumulatorByBrand[b.id] = mappedDrafts;
          } else {
            draftsAccumulatorByBrand[b.id] = [];
          }
          // Collect top posts (supports both { topPosts: [] } and { topPosts: { posts: [] } } shapes)
          const tp = Array.isArray(b?.topPosts) ? b.topPosts : (Array.isArray(b?.topPosts?.posts) ? b.topPosts.posts : []);
          if (Array.isArray(tp) && tp.length > 0) {
            // Attach platform if missing using available context
            tp.forEach((p: any) => {
              if (!p.platform && typeof p.platformType === 'string') p.platform = p.platformType.toLowerCase();
            });
            // Keep a default (first brand) for the "All brands" tab
            setTopPosts(prev => prev.length > 0 ? prev : tp);
            // Also store per-brand to allow brand-tab filtering
            setTopPostsByBrand(prev => ({ ...prev, [b.id]: tp }));
          }
          // Collect personas per brand (preserve brand context)
          if (Array.isArray(b?.personas)) {
            const enriched = b.personas.map((p: any) => ({
              ...p,
              brandId: b.id,
              brandName: b.name,
              brandLogo: b.logo,
            }));
            personasAccumulatorByBrand[b.id] = enriched;
            allPersonasAccumulator.push(...enriched);
          } else {
            personasAccumulatorByBrand[b.id] = [];
          }
        }
        console.log('[Dashboard] Parsed followers brands (normalized)', brandsObj);
        setFollowersData({ brands: brandsObj });
        setRecentDrafts(draftsAccumulator);
        setDraftsByBrand(draftsAccumulatorByBrand);
        setPersonasByBrand(personasAccumulatorByBrand);
        setAllPersonas(allPersonasAccumulator);
        setBrandSummariesByBrand(summariesAccumulatorByBrand);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load');
      } finally {
        const elapsed = Date.now() - startAt;
        const remaining = Math.max(minDelayMs - elapsed, 0);
        const finish = () => { if (!cancelled) setIsLoading(false); };
        if (remaining > 0) {
          timeoutId = setTimeout(finish, remaining);
        } else {
          finish();
        }
        // Cleanup timeout on unmount
        return () => { if (timeoutId) clearTimeout(timeoutId); };
      }
    };
    fetchFollowers();
    return () => { cancelled = true; };
  }, []);

  const brandFollowerCards = useMemo(() => {
    const result: Array<{ brandId: string; brandName: string; brandLogo?: string; totalFollowers: number; platforms: Array<{ platform: string; followers: number | null }> }> = [];
    const brands = followersData?.brands || {};
    for (const [brandId, brandObj] of Object.entries<any>(brands)) {
      const followersObj = brandObj.followers || {};
      const platforms: Array<{ platform: string; followers: number | null }> = [];
      for (const [platform, value] of Object.entries<any>(followersObj)) {
        if (platform === 'total') continue;
        let followers: number | null = null;
        if (Array.isArray(value)) {
          followers = value.reduce((sum: number, v: any) => sum + (typeof v === 'number' ? v : 0), 0);
        } else if (typeof value === 'number') {
          followers = value;
        } else {
          followers = null;
        }
        // Only include platform cards when there is a numeric followers metric
        if (typeof followers === 'number' && !Number.isNaN(followers)) {
          platforms.push({ platform, followers });
        }
      }
      result.push({
        brandId,
        brandName: brandObj.brandName,
        brandLogo: brandObj.brandLogo,
        totalFollowers: (brandObj.followers?.total ?? 0) as number,
        platforms
      });
    }
    const sorted = result.sort((a, b) => (b.totalFollowers || 0) - (a.totalFollowers || 0));
    if (!selectedBrandId) return sorted;
    return sorted.filter(b => b.brandId === selectedBrandId);
  }, [followersData, selectedBrandId]);

  // Total followers for the header card (selected brand if any, otherwise across all brands)
  const totalFollowersHeader = useMemo(() => {
    const brands = followersData?.brands || {};
    if (selectedBrandId && brands[selectedBrandId]) {
      return Number(brands[selectedBrandId]?.followers?.total ?? 0);
    }
    return Object.values<any>(brands).reduce((sum, b) => sum + Number(b?.followers?.total ?? 0), 0);
  }, [followersData, selectedBrandId]);

  // Helpers to layout personas + drafts on same row
  const hasPersonas = selectedBrandId ? ((personasByBrand[selectedBrandId]?.length || 0) > 0) : (allPersonas.length > 0);
  const hasMarketingSummary = Boolean(selectedBrandId && brandSummariesByBrand[selectedBrandId]);

  return (
    <PageLayout
      title={t('dashboard.pageTitle', 'Dashboard')}
      subtitle={t('dashboard.pageSubtitle', 'Have a quick overview of your social media')}
      actions={(
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('sidebar.createContent', 'Create Content')}
        </button>
      )}
    >
      <div className="mb-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-4 rounded-xl border border-primary-200 p-6 bg-primary-50">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">{bannerTitle}</h2>
          <p className="text-gray-700">{bannerSubtitle}</p>
        </div>
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-primary-600 shadow-lg bg-white p-4 h-full flex flex-col justify-center">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium text-gray-500">{t('analytics.metrics.followersTotal', 'Total Followers')}</h3>
            </div>
            <div className="text-4xl font-bold text-gray-900 text-right">
              {isInitialLoading ? (
                <span className="inline-block w-20 h-7 bg-gray-200 rounded animate-pulse" />
              ) : (
                totalFollowersHeader.toLocaleString()
              )}
            </div>
            {/* Removed extra caption per request */}
          </div>
        </div>
      </div>

      {/* Brand Tabs: All Brands + per brand (sorted by number of connected platforms with followers desc) */}
      {Array.isArray(brandsData?.brands) && (brandsData!.brands.length > 1) && (() => {
        const getConnectedCount = (brandId: string): number => {
          const brandObj = (followersData?.brands || {})[brandId];
          const followers = brandObj?.followers || {};
          let count = 0;
          for (const [key, value] of Object.entries<any>(followers)) {
            if (key === 'total') continue;
            let numeric = 0;
            if (Array.isArray(value)) numeric = value.reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0);
            else if (typeof value === 'number') numeric = value;
            if (numeric > 0) count += 1;
          }
          return count;
        };
        const sorted = (brandsData?.brands || []).slice().sort((a: any, b: any) => getConnectedCount(b.id) - getConnectedCount(a.id));
        const tabsBrands = sorted.map((b: any) => ({ id: b.id, name: b.name, logo: b.logo }));
        return (
          <BrandTabs
            activeBrandId={selectedBrandId}
            onBrandChange={setSelectedBrandId}
            brands={tabsBrands}
          />
        );
      })()}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">{error}</div>
      )}
      {isInitialLoading ? (
        <div className="space-y-8">
          {Array(2).fill(0).map((_, i) => (
            <section key={i}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md bg-gray-200 animate-pulse" />
                <div className="h-5 w-64 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {Array(5).fill(0).map((_, j) => (
                  <div key={j} className="rounded-xl p-4 border border-black bg-white">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded bg-gray-200 animate-pulse" />
                      <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <div className="h-7 w-16 rounded bg-gray-200 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-gray-200" />
            </section>
          ))}
        </div>
      ) : (
        <>
        <div className="space-y-8">
          {brandFollowerCards.length === 0 ? (
            <div className="mt-6">
              <LinkBrandRedirectionCard 
                message={t('analytics.connectAccountFirst', 'Connect a social media account to view analytics')}
              />
            </div>
          ) : brandFollowerCards.map((brand) => {
            const platformsOrder = ['youtube', 'facebook', 'instagram', 'linkedin', 'tiktok'] as const;
            const getPlatformValue = (platform: string): number | null => {
              const found = brand.platforms.find(p => p.platform === platform);
              if (!found) return 0; // show card even if missing, default to 0
              return typeof found.followers === 'number' ? found.followers : (found.followers ?? 0);
            };
            const titleFor = (platform: string) => {
              switch (platform) {
                case 'youtube':
                  return t('dashboard.metrics.youtubeSubscribers', 'YouTube Subscribers');
                case 'facebook':
                  return t('dashboard.metrics.facebookFollowers', 'Facebook Followers');
                case 'instagram':
                  return t('dashboard.metrics.instagramFollowers', 'Instagram Followers');
                case 'linkedin':
                  return t('dashboard.metrics.linkedinFollowers', 'LinkedIn Followers');
                case 'tiktok':
                  return t('dashboard.metrics.tiktokFollowers', 'TikTok Followers');
                default:
                  return platform;
              }
            };
            return (
              <section key={brand.brandId}>
                <div className="flex items-center gap-2 mb-3">
                  {brand.brandLogo ? (
                    <img src={brand.brandLogo} alt={brand.brandName} className="w-8 h-8 rounded-md object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                  <h2 className="text-xl font-semibold text-gray-900">{brand.brandName}</h2>
                </div>
                {(() => {
                  const platformsToShow = platformsOrder.filter((pf) => brand.platforms.some((p) => p.platform === pf && typeof p.followers === 'number'));
                  if (platformsToShow.length === 0) {
                    return (
                      <div className="mt-2">
                        <LinkBrandRedirectionCard 
                          message={t('analytics.connectAccountFirst', 'Connect a social media account to view analytics')} 
                        />
                      </div>
                    );
                  }
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {platformsToShow.map((pf) => {
                        const value = getPlatformValue(pf);
                        return (
                          <div key={pf} className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white">
                            <div className="flex items-center gap-2">
                              <div className="p-2 rounded-lg bg-blue-50">
                                <SocialIcon platform={pf as any} size={20} />
                              </div>
                              <span className="text-sm font-medium text-gray-700">{titleFor(pf)}</span>
                            </div>
                            <div className="mt-2 text-3xl font-bold text-gray-900 text-right">{value === null ? ',' : value.toLocaleString()}</div>
                          </div>
                        );
                      })}
                      {!selectedBrandId && (
                        <div className="rounded-xl p-4 border border-primary-600 shadow-lg hover:shadow-xl transition-shadow bg-white">
                          <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-blue-50">
                              <Users className="w-5 h-5 text-gray-700" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">{t('analytics.metrics.followersTotal', 'Total Followers')}</span>
                          </div>
                          <div className="mt-2 text-3xl font-bold text-gray-900 text-right">{(brand.totalFollowers || 0).toLocaleString()}</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const platformsToShow = platformsOrder.filter((pf) => brand.platforms.some((p) => p.platform === pf && typeof p.followers === 'number'));
                  const hasAnyFollowers = platformsToShow.some((pf) => {
                    const value = getPlatformValue(pf);
                    return typeof value === 'number' && value > 0;
                  });
                  if (platformsToShow.length === 0 || !hasAnyFollowers) return null;
                  return (
                    <div className="mt-4 flex justify-center">
                      <a
                        href="/analytics"
                        className="inline-flex items-center px-4 py-2 bg-white border border-primary-600 text-primary-700 rounded-lg shadow-sm hover:bg-primary-50 transition-colors"
                      >
                        <BarChart2 className="w-4 h-4 mr-2" />
                        {t('dashboard.seeAnalytics', 'See my analytics')}
                      </a>
                    </div>
                  );
                })()}
                <div className="mt-6 border-t border-gray-200" />
              </section>
            );
          })}
        </div>
        {/* Drafts and Top Posts section (always render; cards handle empty state) */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <TopPostsCard
              posts={selectedBrandId ? (topPostsByBrand[selectedBrandId] || []) : topPosts}
              onCreate={() => setIsCreateModalOpen(true)}
            />
          </div>
          <div className="lg:col-span-1">
            {selectedBrandId && brandSummariesByBrand[selectedBrandId] ? (
              <BrandSummaryCard data={brandSummariesByBrand[selectedBrandId]} />
            ) : (
              <DraftsCards
                drafts={selectedBrandId ? (draftsByBrand[selectedBrandId] || []) : recentDrafts}
                onCreate={() => setIsCreateModalOpen(true)}
              />
            )}
          </div>
          {/* Personas shown here only when there is no Marketing Strategy card to show below */}
          {!hasMarketingSummary && (selectedBrandId ? (personasByBrand[selectedBrandId]?.length || 0) > 0 : allPersonas.length > 0) && (
            <div className="lg:col-span-3">
              <PersonasCard personas={selectedBrandId ? (personasByBrand[selectedBrandId] || []) : allPersonas} />
            </div>
          )}
        </div>
        {hasMarketingSummary && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              {hasPersonas ? (
                <PersonasCard personas={selectedBrandId ? (personasByBrand[selectedBrandId] || []) : allPersonas} />
              ) : null}
            </div>
            <div className={hasPersonas ? 'lg:col-span-2' : 'lg:col-span-3'}>
              <DraftsCards
                drafts={selectedBrandId ? (draftsByBrand[selectedBrandId] || []) : recentDrafts}
                onCreate={() => setIsCreateModalOpen(true)}
              />
            </div>
          </div>
        )}
        {/* Create Post Modal */}
        <CreatePostModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreateWithAI={() => { setIsCreateModalOpen(false); window.location.href = '/content-creation'; }}
          onCreateManually={() => { setIsCreateModalOpen(false); window.location.href = '/posts/new'; }}
        />
        </>
      )}
    </PageLayout>
  );
};

export default Dashboard; 